import { Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense, type ComponentType } from 'react'
import { AuthLayout, DashboardLayout } from '@/components/layout'
import { ProtectedRoute } from './protected-route'
import { PageLoader } from '@/components/shared/loading-spinner'
import { useAuth } from '@/providers/auth-provider'

/**
 * Wraps React.lazy so that a failed dynamic import — typically a stale chunk
 * left over after a new deploy replaced the hashed asset filenames — triggers a
 * one-time full page reload to fetch the fresh index.html and chunk map. A
 * sessionStorage flag guards against reload loops if the failure is genuine.
 */
function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    const RELOAD_KEY = 'chunk-reload-attempted'
    try {
      const component = await factory()
      window.sessionStorage.removeItem(RELOAD_KEY)
      return component
    } catch (error) {
      const alreadyReloaded = window.sessionStorage.getItem(RELOAD_KEY)
      if (!alreadyReloaded) {
        window.sessionStorage.setItem(RELOAD_KEY, 'true')
        window.location.reload()
        // Return a never-resolving promise so nothing renders before reload.
        return new Promise<{ default: T }>(() => {})
      }
      throw error
    }
  })
}

// Auth pages
const LoginPage = lazyWithRetry(() => import('@/pages/auth/login'))
const RegisterPage = lazyWithRetry(() => import('@/pages/auth/register'))
const ForgotPasswordPage = lazyWithRetry(() => import('@/pages/auth/forgot-password'))
const ResetPasswordPage = lazyWithRetry(() => import('@/pages/auth/reset-password'))
const JoinTeamPage = lazyWithRetry(() => import('@/pages/auth/join-team'))
const SetupPasswordPage = lazyWithRetry(() => import('@/pages/auth/setup-password'))

// Customer pages
const CustomerDashboard = lazyWithRetry(() => import('@/pages/customer/dashboard'))
const CustomerPets = lazyWithRetry(() => import('@/pages/customer/pets'))
const CustomerPetDetail = lazyWithRetry(() => import('@/pages/customer/pets/detail'))
const CustomerReservations = lazyWithRetry(() => import('@/pages/customer/reservations'))
const CustomerNewReservation = lazyWithRetry(() => import('@/pages/customer/reservations/new'))
const CustomerReservationDetail = lazyWithRetry(() => import('@/pages/customer/reservations/detail'))
const CustomerInvoices = lazyWithRetry(() => import('@/pages/customer/invoices'))
const CustomerInvoiceDetail = lazyWithRetry(() => import('@/pages/customer/invoices/detail'))
const CustomerProfile = lazyWithRetry(() => import('@/pages/customer/profile'))
const PendingApproval = lazyWithRetry(() => import('@/pages/customer/pending-approval'))
const Onboarding = lazyWithRetry(() => import('@/pages/onboarding'))

// Admin pages
const AdminDashboard = lazyWithRetry(() => import('@/pages/admin/dashboard'))
const AdminCustomers = lazyWithRetry(() => import('@/pages/admin/customers'))
const AdminCustomerDetail = lazyWithRetry(() => import('@/pages/admin/customers/detail'))
const AdminPets = lazyWithRetry(() => import('@/pages/admin/pets'))
const AdminPetDetail = lazyWithRetry(() => import('@/pages/admin/pets/detail'))
const AdminReservations = lazyWithRetry(() => import('@/pages/admin/reservations'))
const AdminReservationDetail = lazyWithRetry(() => import('@/pages/admin/reservations/detail'))
const AdminCalendar = lazyWithRetry(() => import('@/pages/admin/calendar'))
const AdminRunSheet = lazyWithRetry(() => import('@/pages/admin/run-sheet'))
const AdminInvoices = lazyWithRetry(() => import('@/pages/admin/invoices'))
const AdminPayments = lazyWithRetry(() => import('@/pages/admin/payments'))
const AdminReceivables = lazyWithRetry(() => import('@/pages/admin/receivables'))
const AdminReports = lazyWithRetry(() => import('@/pages/admin/reports'))
const AdminServices = lazyWithRetry(() => import('@/pages/admin/services'))
const AdminAgreements = lazyWithRetry(() => import('@/pages/admin/agreements'))
const AdminKennels = lazyWithRetry(() => import('@/pages/admin/kennels'))
const AdminInvoiceCreate = lazyWithRetry(() => import('@/pages/admin/invoices/create'))
const AdminInvoiceDetail = lazyWithRetry(() => import('@/pages/admin/invoices/detail'))
const AdminStaff = lazyWithRetry(() => import('@/pages/admin/staff'))
const AdminSettings = lazyWithRetry(() => import('@/pages/admin/settings'))
const AdminEmails = lazyWithRetry(() => import('@/pages/admin/emails'))

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>
}

export function AppRoutes() {
  const { user, isStaff, isLoading } = useAuth()

  // Don't make any routing decisions until auth state is fully resolved
  if (isLoading) {
    return <PageLoader />
  }

  return (
    <Routes>
      {/* Root redirect */}
      <Route
        path="/"
        element={
          <Navigate to={user ? (isStaff ? '/admin' : '/dashboard') : '/auth/login'} replace />
        }
      />

      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route
          path="/auth/login"
          element={
            user ? (
              <Navigate to={isStaff ? '/admin' : '/dashboard'} replace />
            ) : (
              <SuspenseWrapper>
                <LoginPage />
              </SuspenseWrapper>
            )
          }
        />
        <Route
          path="/auth/register"
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <SuspenseWrapper>
                <RegisterPage />
              </SuspenseWrapper>
            )
          }
        />
        <Route
          path="/auth/forgot-password"
          element={
            <SuspenseWrapper>
              <ForgotPasswordPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/auth/reset-password"
          element={
            <SuspenseWrapper>
              <ResetPasswordPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/auth/join-team"
          element={
            <SuspenseWrapper>
              <JoinTeamPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/auth/setup-password"
          element={
            <SuspenseWrapper>
              <SetupPasswordPage />
            </SuspenseWrapper>
          }
        />
      </Route>

      {/* Pending approval page */}
      <Route
        path="/pending-approval"
        element={
          <ProtectedRoute requireApproval={false}>
            <SuspenseWrapper>
              <PendingApproval />
            </SuspenseWrapper>
          </ProtectedRoute>
        }
      />

      {/* Onboarding wizard */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <SuspenseWrapper>
              <Onboarding />
            </SuspenseWrapper>
          </ProtectedRoute>
        }
      />

      {/* Customer routes */}
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route
          path="/dashboard"
          element={
            <SuspenseWrapper>
              <CustomerDashboard />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/pets"
          element={
            <SuspenseWrapper>
              <CustomerPets />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/pets/:id"
          element={
            <SuspenseWrapper>
              <CustomerPetDetail />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reservations"
          element={
            <SuspenseWrapper>
              <CustomerReservations />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reservations/:id"
          element={
            <SuspenseWrapper>
              <CustomerReservationDetail />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reservations/new"
          element={
            <SuspenseWrapper>
              <CustomerNewReservation />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/invoices"
          element={
            <SuspenseWrapper>
              <CustomerInvoices />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/invoices/:id"
          element={
            <SuspenseWrapper>
              <CustomerInvoiceDetail />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/profile"
          element={
            <SuspenseWrapper>
              <CustomerProfile />
            </SuspenseWrapper>
          }
        />
      </Route>

      {/* Admin routes */}
      <Route
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route
          path="/admin"
          element={
            <SuspenseWrapper>
              <AdminDashboard />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/customers"
          element={
            <SuspenseWrapper>
              <AdminCustomers />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/customers/:id"
          element={
            <SuspenseWrapper>
              <AdminCustomerDetail />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/pets"
          element={
            <SuspenseWrapper>
              <AdminPets />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/pets/:id"
          element={
            <SuspenseWrapper>
              <AdminPetDetail />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/reservations"
          element={
            <SuspenseWrapper>
              <AdminReservations />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/reservations/:id"
          element={
            <SuspenseWrapper>
              <AdminReservationDetail />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/calendar"
          element={
            <SuspenseWrapper>
              <AdminCalendar />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/run-sheet"
          element={
            <SuspenseWrapper>
              <AdminRunSheet />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/kennels"
          element={
            <SuspenseWrapper>
              <AdminKennels />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/invoices"
          element={
            <SuspenseWrapper>
              <AdminInvoices />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/invoices/create"
          element={
            <SuspenseWrapper>
              <AdminInvoiceCreate />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/invoices/:id"
          element={
            <SuspenseWrapper>
              <AdminInvoiceDetail />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/payments"
          element={
            <SuspenseWrapper>
              <AdminPayments />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/receivables"
          element={
            <SuspenseWrapper>
              <AdminReceivables />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <SuspenseWrapper>
              <AdminReports />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/services"
          element={
            <SuspenseWrapper>
              <AdminServices />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/agreements"
          element={
            <SuspenseWrapper>
              <AdminAgreements />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/staff"
          element={
            <SuspenseWrapper>
              <AdminStaff />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/emails"
          element={
            <SuspenseWrapper>
              <AdminEmails />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <SuspenseWrapper>
              <AdminSettings />
            </SuspenseWrapper>
          }
        />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
